import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { formatDateTime } from './utils/dateFormatter';
import { useAccessGroups } from './ResourcesRouter.js';
import { UnsavedChangesGuard, useUnsavedChangesGuard } from './components/UnsavedChangesGuard.js';
import { fetchResources } from './resources.actions.js';
import {
	Card,
	CardBody,
	CardHeader,
	CardFooter,
	Button,
	Chip,
	Table,
	TableHeader,
	TableBody,
	TableColumn,
	TableRow,
	TableCell,
	Skeleton,
	Modal,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	useDisclosure,
	addToast,
	Input,
	Select,
	SelectItem,
	Checkbox
} from '@heroui/react';
import {
	Monitor,
	Edit,
	Trash2,
	Gauge,
	Clock,
	X,
	Save,
	ChevronRight
} from 'lucide-react';
import ResourcesApi from '../../api/resourcesApi.js';
import SimulatorsApi from '../../api/simulatorsApi.js';
import HeroUIBreadcrumb from '../breadcrumbs/HeroUIBreadcrumb.js';

// Pay status options (from php/models/Resources.php)
const PAY_STATUSES = [
	{ value: '', name: 'Not Applicable' },
	{ value: 'perhour', name: 'By Meter' },
	{ value: 'peruse', name: 'Per Use' },
	{ value: 'perblock', name: 'Scheduled Time' }
];

// Get organization unique from URL
const getOrgUnique = () => {
	const match = window.location.pathname.match(/\/org\/([^/]+)\//);
	return match ? match[1] : '';
};

function ViewSimulatorPageContent() {
	const { simulatorId } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const accessGroups = useAccessGroups();
	const { setDirty, navigateWithCheck } = useUnsavedChangesGuard();
	const orgUnique = getOrgUnique();

	const [simulator, setSimulator] = React.useState(null);
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState(null);
	const [isEditing, setIsEditing] = React.useState(false);
	const [isSaving, setIsSaving] = React.useState(false);
	const [editData, setEditData] = React.useState({});
	const [locations, setLocations] = React.useState([]);
	const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

	// Fetch locations for dropdown
	React.useEffect(() => {
		const fetchLocations = async () => {
			try {
				const response = await ResourcesApi.getLocations();
				setLocations(response.data?.data || response.data || []);
			} catch (err) {
				console.error('Failed to load locations:', err);
			}
		};
		fetchLocations();
	}, []);

	// Fetch simulator data
	const fetchSimulatorData = React.useCallback(async () => {
		if (!simulatorId) return;
		setLoading(true);
		setError(null);
		try {
			const response = await ResourcesApi.getSimulator(parseInt(simulatorId, 10));
			const data = response.data?.data || response.data || response;
			setSimulator(data);
		} catch (err) {
			const errorMessage = 'Failed to load simulator data';
			setError(errorMessage);
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
		} finally {
			setLoading(false);
		}
	}, [simulatorId]);

	React.useEffect(() => {
		fetchSimulatorData();
	}, [fetchSimulatorData]);


	// Format change type for display
	const formatChangeType = changeType => {
		switch (changeType) {
			case 'checkIn':
				return { label: 'Check In', color: 'success' };
			case 'checkOut':
				return { label: 'Check Out', color: 'warning' };
			case 'adjustment':
				return { label: 'Adjustment', color: 'primary' };
			case 'maintenance':
				return { label: 'Maintenance', color: 'secondary' };
			default:
				return { label: changeType || 'Unknown', color: 'default' };
		}
	};


	// Helper to get location ID from resource (handles both SSR and API format)
	const getLocationId = resource => {
		if (!resource) return null;
		if (resource.locationID) return resource.locationID;
		if (resource.location) {
			return typeof resource.location === 'object' ? resource.location.id : resource.location;
		}
		return null;
	};

	// Helper to get location name from resource
	const getLocationName = resource => {
		if (!resource) return '';
		if (resource.locationName) return resource.locationName;
		if (resource.location && typeof resource.location === 'object') {
			return resource.location.name;
		}
		return '';
	};

	// Handle edit - toggle inline editing mode
	const handleEdit = () => {
		// Get location ID from API response
		const locationId = getLocationId(simulator?.resources?.[0]) ||
			simulator?.location?.id ||
			'';

		setEditData({
			name: simulator.name || '',
			registrationNumber: simulator.registrationNumber || '',
			locationId: locationId ? String(locationId) : '',
			payStatus: simulator?.resources?.[0]?.payStatus || '',
			rate: simulator?.resources?.[0]?.rate || '',
			enableHobbs: simulator.enableHobbs || false,
			hobbsValue: simulator.hobbsValue || '',
			enableRate: simulator.enableRate || false,
			hobbsRate: simulator.hobbsRate || ''
		});

		setIsEditing(true);
	};

	// Handle cancel edit
	const handleCancelEdit = () => {
		setDirty(false);
		setIsEditing(false);
		setEditData({});
	};

	// Handle edit field change
	const handleEditChange = (field, value) => {
		if (editData[field] === value) return;

		setEditData(prev => {
			const newData = { ...prev, [field]: value };
			// Clear rate if no charge type selected
			if (field === 'payStatus' && !value) {
				newData.rate = '';
			}
			// Reset hobbs value when disabling
			if (field === 'enableHobbs' && !value) {
				newData.hobbsValue = '0';
			}
			// Reset hobbs rate when disabling
			if (field === 'enableRate' && !value) {
				newData.hobbsRate = '';
			}
			return newData;
		});
		setDirty(true);
	};

	// Handle save
	const handleSave = async () => {
		// Validate required fields
		if (!editData.name?.trim()) {
			addToast({
				title: 'Validation Error',
				description: 'Simulator name is required',
				color: 'danger'
			});
			return;
		}

		if (!editData.locationId) {
			addToast({
				title: 'Validation Error',
				description: 'Location is required',
				color: 'danger'
			});
			return;
		}

		setIsSaving(true);
		try {
			const payload = {
				name: editData.name.trim(),
				registrationNumber: editData.registrationNumber?.trim() || null,
				resourceLocationId: editData.locationId ? parseInt(editData.locationId, 10) : null,
				resourcePayStatus: editData.payStatus || null,
				resourceRate: editData.rate?.toString().trim() || null,
				enableHobbs: editData.enableHobbs,
				hobbsValue: editData.enableHobbs && editData.hobbsValue ? parseFloat(editData.hobbsValue) : 0,
				enableRate: editData.enableRate,
				hobbsRate: editData.enableRate && editData.hobbsRate ? editData.hobbsRate.toString() : null
			};

			await SimulatorsApi.updateSimulator(parseInt(simulatorId, 10), payload);

			addToast({
				title: 'Success',
				description: 'Simulator updated successfully',
				color: 'success'
			});

			setDirty(false);
			setIsEditing(false);
			fetchSimulatorData(); // Refresh data
		} catch (err) {
			const errorMessage =
				err.response?.data?.error?.[0] ||
				err.response?.data?.message ||
				err.message ||
				'Failed to update simulator';
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
		} finally {
			setIsSaving(false);
		}
	};

	// Handle delete
	const handleDelete = async () => {
		try {
			await SimulatorsApi.deleteSimulator(parseInt(simulatorId, 10));
			dispatch(fetchResources());
			addToast({
				title: 'Success',
				description: 'Simulator deleted successfully',
				color: 'success'
			});
			onDeleteClose();
			navigate('/');
		} catch (err) {
			const errorMessage =
				err.response?.data?.error?.[0] ||
				err.response?.data?.message ||
				err.message ||
				'Failed to delete simulator';
			addToast({
				title: 'Cannot Delete',
				description: errorMessage,
				color: 'danger'
			});
		}
	};

	// Navigate to meters page
	const handleViewMeters = () => {
		navigateWithCheck(`/simulator/${simulatorId}/meters`);
	};

	// Back navigation
	const handleBack = () => {
		navigateWithCheck('/');
	};

	// Breadcrumbs
	const isHangars = window.location.pathname.includes('/hangars');
	const breadcrumbCustomItems = [{ label: isHangars ? 'Aircraft Status' : 'Resources', href: `/org/${orgUnique}/${isHangars ? 'hangars' : 'resources'}/` }];

	// Loading skeleton
	if (loading) {
		return (
			<div className="max-w-5xl mx-auto px-4 py-8">
				<Skeleton className="h-8 w-48 rounded-lg mb-6" />
				<Skeleton className="h-24 w-full rounded-lg mb-4" />
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<Skeleton className="h-48 w-full rounded-lg" />
					<Skeleton className="h-48 w-full rounded-lg" />
				</div>
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div className="max-w-5xl mx-auto px-4 py-8">
				<HeroUIBreadcrumb currentPage="Simulator" customItems={breadcrumbCustomItems} />
				<div className="text-center py-16">
					<Monitor className="w-16 h-16 text-danger mx-auto mb-4" />
					<p className="text-danger text-lg mb-4">{error}</p>
					<Button color="primary" variant="flat" onPress={fetchSimulatorData}>
						Try Again
					</Button>
				</div>
			</div>
		);
	}

	const counters = simulator?.counters || [];
	const histories = simulator?.histories || [];

	return (
		<div className="max-w-5xl mx-auto px-4 py-8">
			{/* Breadcrumbs */}
			<HeroUIBreadcrumb
				currentPage={simulator?.name || 'View Simulator'}
				customItems={breadcrumbCustomItems}
			/>

			{/* Page Header */}
			<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mt-6 mb-8">
				<div className="flex items-center gap-4">
					<div className="p-3 rounded-full bg-gradient-to-br from-violet-500 to-purple-600">
						<Monitor className="w-8 h-8 text-white" />
					</div>
					<div>
						<h1 className="text-2xl font-bold">{simulator?.name || 'Simulator'}</h1>
						<p className="text-default-500 text-sm">
							{simulator?.registrationNumber}
							{simulator?.manufacturer || simulator?.model ? ' · ' : ''}
							{[simulator?.manufacturer, simulator?.model].filter(Boolean).join(' ')}
							{simulator?.registrationNumber || simulator?.manufacturer || simulator?.model ? '' : 'Simulator Details'}
						</p>
					</div>
				</div>

				{/* Action Buttons */}
				<div className="flex flex-wrap gap-3 self-end md:self-auto">
					{isEditing ? (
						<>
							<Button
								variant="bordered"
								onPress={handleCancelEdit}
								isDisabled={isSaving}
							>
								Cancel
							</Button>
							<Button
								color="primary"
								startContent={<Save className="w-4 h-4" />}
								onPress={handleSave}
								isLoading={isSaving}
							>
								Save Changes
							</Button>
						</>
					) : (accessGroups._loaded && accessGroups.resourcesManage) ? (
						<>
							<Button
								color="danger"
								variant="light"
								startContent={<Trash2 className="w-4 h-4" />}
								onPress={onDeleteOpen}
							>
								Delete
							</Button>
							<Button
								color="primary"
								startContent={<Edit className="w-4 h-4" />}
								onPress={handleEdit}
							>
								Edit Simulator
							</Button>
						</>
					) : null}
					<Button variant="light" isIconOnly onPress={handleBack}>
						<X className="w-5 h-5" />
					</Button>
				</div>
			</div>

			{/* Content Grid */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* Simulator Information Card */}
				<Card className="border border-default-200 shadow-md bg-gradient-to-br from-white to-violet-50/30">
					<CardHeader className="bg-gradient-to-r from-violet-100/80 via-violet-50/50 to-transparent pb-3 border-b border-violet-100/50">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-violet-500/10 ring-1 ring-violet-500/20">
								<Monitor className="w-5 h-5 text-violet-600" />
							</div>
							<span className="text-lg font-semibold text-violet-900">Simulator Information</span>
						</div>
					</CardHeader>
					<CardBody className="pt-4">
						<div className="space-y-4">
							{/* Simulator Name */}
							<div>
								<label className="text-sm font-medium text-default-600">Simulator Name</label>
								{isEditing ? (
									<Input
										value={editData.name}
										onValueChange={value => handleEditChange('name', value)}
										placeholder="Enter simulator name"
										variant="flat"
										size="sm"
										isRequired
										className="mt-1"
									/>
								) : (
									<p className="font-medium">{simulator?.name || '-'}</p>
								)}
							</div>

							{/* Registration Number */}
							<div>
								<label className="text-sm font-medium text-default-600">Registration Number</label>
								{isEditing ? (
									<Input
										value={editData.registrationNumber}
										onValueChange={value => handleEditChange('registrationNumber', value)}
										placeholder="Enter registration number"
										variant="flat"
										size="sm"
										className="mt-1"
									/>
								) : (
									<p className="font-medium">{simulator?.registrationNumber || '-'}</p>
								)}
							</div>

							{/* Location */}
							<div>
								<label className="text-sm font-medium text-default-600">Location <span className="text-danger">*</span></label>
								{isEditing ? (
									<Select
										aria-label="Location"
										selectedKeys={editData.locationId ? [editData.locationId] : []}
										onSelectionChange={keys => handleEditChange('locationId', Array.from(keys)[0])}
										placeholder="Select location"
										variant="flat"
										size="sm"
										isRequired
										className="mt-1"
									>
										{locations.map(location => (
											<SelectItem key={String(location.id)}>
												{location.city ? `${location.name}, ${location.city}` : location.name}
											</SelectItem>
										))}
									</Select>
								) : (
									<p className="font-medium">
										{getLocationName(simulator?.resources?.[0]) ||
										 simulator?.location?.name || '-'}
									</p>
								)}
							</div>

							{/* Charge Type */}
							<div>
								<label className="text-sm font-medium text-default-600">Charge Type</label>
								{isEditing ? (
									<Select
										aria-label="Charge Type"
										selectedKeys={editData.payStatus ? [editData.payStatus] : ['none']}
										onSelectionChange={keys => {
											const value = Array.from(keys)[0];
											handleEditChange('payStatus', value === 'none' ? '' : value);
										}}
										placeholder="Select charge type"
										variant="flat"
										size="sm"
										className="mt-1"
									>
										<SelectItem key="none">Not Applicable</SelectItem>
										{PAY_STATUSES.filter(s => s.value).map(status => (
											<SelectItem key={status.value}>{status.name}</SelectItem>
										))}
									</Select>
								) : (
									<p className="font-medium">
										{PAY_STATUSES.find(s => s.value === simulator?.resources?.[0]?.payStatus)?.name || 'Not Applicable'}
									</p>
								)}
							</div>

							{/* Rate (shown when payStatus is set) */}
							{(isEditing ? editData.payStatus : simulator?.resources?.[0]?.payStatus) && (
								<div>
									<label className="text-sm font-medium text-default-600">Rate</label>
									{isEditing ? (
										<Input
											value={editData.rate}
											onValueChange={value => handleEditChange('rate', value)}
											placeholder="Enter rate"
											variant="flat"
											size="sm"
											type="number"
											step="0.01"
											min="0"
											startContent="$"
											className="mt-1"
										/>
									) : (
										<p className="font-medium">
											{simulator?.resources?.[0]?.rate ? `$${simulator?.resources?.[0]?.rate}` : '-'}
										</p>
									)}
								</div>
							)}

							{/* Hobbs Enabled */}
							<div>
								{isEditing ? (
									<div className="p-3 bg-default-100 rounded-lg space-y-3">
										<div className="flex items-center justify-between">
											<Checkbox
												isSelected={editData.enableHobbs}
												onValueChange={value => handleEditChange('enableHobbs', value)}
												size="sm"
											>
												Enable Hobbs
											</Checkbox>
											{editData.enableHobbs && (
												<Input
													aria-label="Hobbs Value"
													value={editData.hobbsValue}
													onValueChange={value => handleEditChange('hobbsValue', value)}
													placeholder="0"
													variant="flat"
													type="text"
													size="sm"
													className="w-24"
													classNames={{ input: 'text-right' }}
												/>
											)}
										</div>

										{/* Rate ($)/Hr */}
										<div className="flex items-center justify-between">
											<Checkbox
												isSelected={editData.enableRate}
												onValueChange={value => handleEditChange('enableRate', value)}
												size="sm"
											>
												Rate ($)/Hr
											</Checkbox>
											{editData.enableRate && (
												<Input
													aria-label="Hobbs Rate"
													value={editData.hobbsRate}
													onValueChange={value => handleEditChange('hobbsRate', value)}
													placeholder="0.00"
													variant="flat"
													type="text"
													size="sm"
													className="w-24"
													startContent="$"
													classNames={{ input: 'text-right' }}
												/>
											)}
										</div>
									</div>
								) : (
									<>
										<div className="flex items-center gap-2">
											<label className="text-sm font-medium text-default-600">Hobbs Enabled</label>
											<Chip
												size="sm"
												color={simulator?.enableHobbs ? 'success' : 'default'}
												variant="flat"
											>
												{simulator?.enableHobbs ? 'Yes' : 'No'}
											</Chip>
										</div>
										{simulator?.enableHobbs && (
											<div className="mt-3">
												<label className="text-sm font-medium text-default-600">Hobbs Value</label>
												<p className="font-medium text-lg">{simulator?.hobbsValue || 0}</p>
											</div>
										)}
										{simulator?.hobbsRate && (
											<div className="mt-3">
												<label className="text-sm font-medium text-default-600">Hobbs Rate</label>
												<p className="font-medium">${simulator.hobbsRate}/hr</p>
											</div>
										)}
									</>
								)}
							</div>
						</div>
					</CardBody>
				</Card>

				{/* Counters Card */}
				<Card className="border border-default-200 shadow-md bg-gradient-to-br from-white to-cyan-50/30">
					<CardHeader className="bg-gradient-to-r from-cyan-100/80 via-cyan-50/50 to-transparent pb-3 border-b border-cyan-100/50">
						<div className="flex items-center gap-3">
							<div className="p-2 rounded-lg bg-cyan-500/10 ring-1 ring-cyan-500/20">
								<Gauge className="w-5 h-5 text-cyan-600" />
							</div>
							<span className="text-lg font-semibold text-cyan-900">Counters</span>
						</div>
					</CardHeader>
					<CardBody className="pt-4">
						{counters.length > 0 ? (
							<div className="space-y-3">
								{counters.map((counter, index) => (
									<div
										key={counter.id || index}
										className="flex justify-between items-center p-3 rounded-lg bg-default-100"
									>
										<div>
											<p className="font-medium">{counter.name}</p>
											{counter.type && (
												<p className="text-xs text-default-500 capitalize">{counter.type}</p>
											)}
										</div>
										<span className="text-xl font-bold">{counter.value || 0}</span>
									</div>
								))}
							</div>
						) : (
							<p className="text-default-500 text-center py-4">No counters configured</p>
						)}
					</CardBody>
					{counters.length > 0 && (
						<CardFooter className="py-2 border-t border-cyan-100/50">
							<Button
								variant="light"
								size="sm"
								className="w-full text-cyan-600 hover:bg-cyan-50"
								endContent={<ChevronRight className="w-4 h-4" />}
								onPress={handleViewMeters}
							>
								{accessGroups._loaded && !accessGroups.resourcesHistory ? 'View Meters' : 'View History'}
							</Button>
						</CardFooter>
					)}
				</Card>
			</div>

			{/* History Table */}
			<Card className="mt-6 border border-default-200 shadow-md bg-gradient-to-br from-white to-slate-50/30">
				<CardHeader className="flex justify-between items-center bg-gradient-to-r from-slate-100/80 via-slate-50/50 to-transparent pb-3 border-b border-slate-100/50">
					<div className="flex items-center gap-3">
						<div className="p-2 rounded-lg bg-slate-500/10 ring-1 ring-slate-500/20">
							<Clock className="w-5 h-5 text-slate-600" />
						</div>
						<span className="text-lg font-semibold text-slate-900">Value History</span>
					</div>
					<Chip size="sm" variant="flat">
						{histories.length} entries
					</Chip>
				</CardHeader>
				<CardBody className="pt-4">
					{histories.length > 0 ? (
						<Table
							aria-label="Simulator history table"
							removeWrapper
							classNames={{
								tbody:
									'[&>tr:nth-child(even)>td]:bg-default-100 [&>tr:nth-child(even)>td:first-child]:rounded-l-lg [&>tr:nth-child(even)>td:last-child]:rounded-r-lg'
							}}
						>
							<TableHeader>
								<TableColumn>DATE</TableColumn>
								<TableColumn>TYPE</TableColumn>
								<TableColumn>VALUE</TableColumn>
								<TableColumn>DIFF</TableColumn>
								<TableColumn>COMMENT</TableColumn>
							</TableHeader>
							<TableBody>
								{histories.map((entry, index) => {
									const changeTypeInfo = formatChangeType(entry.changeType);
									return (
										<TableRow key={entry.id || index}>
											<TableCell>
												{formatDateTime(entry.dateOfChange)}
											</TableCell>
											<TableCell>
												<Chip
													size="sm"
													color={changeTypeInfo.color}
													variant="flat"
												>
													{changeTypeInfo.label}
												</Chip>
											</TableCell>
											<TableCell className="font-medium">
												{entry.value}
											</TableCell>
											<TableCell>
												{entry.diff !== undefined && entry.diff !== null && (
													<span
														className={
															entry.diff > 0
																? 'text-success'
																: entry.diff < 0
																	? 'text-danger'
																	: ''
														}
													>
														{entry.diff > 0 ? '+' : ''}
														{entry.diff}
													</span>
												)}
											</TableCell>
											<TableCell className="max-w-xs truncate">
												{entry.comment || '-'}
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					) : (
						<p className="text-default-500 text-center py-8">No history records found</p>
					)}
				</CardBody>
			</Card>

			{/* Delete Confirmation Modal */}
			<Modal scrollBehavior="inside" isOpen={isDeleteOpen} onClose={onDeleteClose}>
				<ModalContent>
					{onClose => (
						<>
							<ModalHeader className="flex flex-col gap-1">Delete Simulator</ModalHeader>
							<ModalBody>
								<p>
									Are you sure you want to delete simulator{' '}
									<strong>{simulator?.name}</strong>? This action cannot be undone.
								</p>
							</ModalBody>
							<ModalFooter>
								<Button color="default" variant="light" onPress={onClose}>
									Cancel
								</Button>
								<Button color="danger" onPress={handleDelete}>
									Delete Simulator
								</Button>
							</ModalFooter>
						</>
					)}
				</ModalContent>
			</Modal>
		</div>
	);
}

function ViewSimulatorPage() {
	return (
		<UnsavedChangesGuard>
			<ViewSimulatorPageContent />
		</UnsavedChangesGuard>
	);
}

export default ViewSimulatorPage;
